import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FeaturedBig from "./FeaturedBig.jsx";
import EventCard from "./EventCard.jsx";
import { API } from "../config/api.js";
import { get } from "../lib/http.js";
import {
  getEventScheduleParts,
  formatEventScheduleLabel,
  resolveEventPosterUrl,
  canBookEvent,
  isPremiumEligibleEvent,
  resolveEventLandscapeUrl,
} from "../api/events.js";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

const AUTOPLAY_MS = 3000;

export default function FeaturedCarousel() {
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(0);
  const [anim, setAnim] = useState(true);
  const [paused, setPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);

  const handleNativeScroll = (e) => {
    // Moved below slides definition to prevent ReferenceError
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await get(`${API.events.getEvent}`, {
          headers: { "Content-Type": "application/json" },
        });
        const all = Array.isArray(res?.events) ? res.events : [];

        const eligible = all.filter((e) => isPremiumEligibleEvent(e));
        // Keep all active events in carousel so users can slide through all options.
        const list = eligible;

        if (alive) {
          setEvents(list);
          setActive(0);
        }
      } catch (e) {
        console.error("FeaturedCarousel fetch error:", e);
        if (alive) setEvents([]);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
      clearInterval(timerRef.current);
    };
  }, []);

  const slides = useMemo(
    () =>
      events.map((ev) => {
        const schedule = getEventScheduleParts(ev);

        return {
          id: ev?._id || ev?.id,
          title: ev?.name || ev?.title || "Event",
          subtitle: ev?.venue?.name
            ? `${ev.venue.name}${ev?.venue?.city ? `, ${ev.venue.city}` : ""}`
            : ev?.location || "",
          location: ev?.venue?.name
            ? `${ev.venue.name}${ev?.venue?.city ? `, ${ev.venue.city}` : ""}`
            : ev?.location || "",
          image: resolveEventPosterUrl(ev),
          landscapeImage: resolveEventLandscapeUrl(ev),
          category: ev?.category || ev?.type || "Featured",
          about: ev?.description || "",
          duration:
            typeof ev?.duration === "number"
              ? `${Math.floor(ev.duration / 60)}h ${ev.duration % 60}m`
              : ev?.duration || "",
          schedule,
          dateBlock:
            schedule.allDaysPass && schedule.dates.length
              ? { date: schedule.dates[0], time: schedule.time || "" }
              : schedule.dates.length === 1
              ? { date: schedule.dates[0], time: schedule.time || "" }
              : schedule.dates.length > 1
              ? { date: schedule.dates.join(" & "), time: schedule.time || "" }
              : undefined,
          dateFull: schedule.full || formatEventScheduleLabel(ev),
          languages: ev?.language,
          age: ev?.ageRestriction ? `${ev.ageRestriction}+` : undefined,
          status: ev?.status,
          canBook: canBookEvent(ev),
          price: ev?.ticketCategories?.filter(t => t?.isTicketAvailable !== false).map(t => Number(t.price)).filter(n => !Number.isNaN(n)).sort((a,b)=>a-b)[0] || 0
        };
      }),
    [events]
  );

  const renderSlides = useMemo(() => {
    if (slides.length <= 1) return slides;
    return [slides[slides.length - 1], ...slides, slides[0]];
  }, [slides]);

  const safeActive = slides.length > 0 ? ((active % slides.length) + slides.length) % slides.length : 0;
  const trackIndex = slides.length > 1 ? active + 1 : 0;

  const actualHandleNativeScroll = (e) => {
    if (window.innerWidth >= 768) return;
    const container = e.target;
    const center = container.scrollLeft + container.clientWidth / 2;
    let minDiff = Infinity;
    let newActive = safeActive;

    Array.from(container.children).forEach((child, idx) => {
      const childCenter = child.offsetLeft + child.clientWidth / 2;
      const diff = Math.abs(center - childCenter);
      if (diff < minDiff) {
        minDiff = diff;
        newActive = idx;
      }
    });

    if (newActive !== safeActive) {
      setActive(newActive);
    }
  };

  useEffect(() => {
    if (!slides.length || paused || slides.length <= 1) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setAnim(true);
      setActive((prev) => prev + 1);
    }, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [slides.length, paused]);

  // Sync mobile scroll
  useEffect(() => {
    if (window.innerWidth >= 768 || !scrollRef.current) return;
    const child = scrollRef.current.children[safeActive];
    if (child) {
      scrollRef.current.scrollTo({
        left: child.offsetLeft - scrollRef.current.clientWidth / 2 + child.clientWidth / 2,
        behavior: "smooth",
      });
    }
  }, [safeActive]);

  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  if (!loaded || !slides.length) return null;

  const onOpen = (id) => {
    setPaused(true);
    if (id) nav(`/events/${id}`);
  };

  const handlePrev = (e) => {
    e?.stopPropagation();
    setAnim(true);
    setActive((prev) => prev - 1);
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    setAnim(true);
    setActive((prev) => prev + 1);
  };

  const handleTransitionEnd = () => {
    if (slides.length <= 1) return;
    if (active === -1) {
      setAnim(false);
      setActive(slides.length - 1);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    } else if (active === slides.length) {
      setAnim(false);
      setActive(0);
      requestAnimationFrame(() => requestAnimationFrame(() => setAnim(true)));
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrev({ stopPropagation: () => {} });
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  return (
    <div className="w-full dark:bg-slate-950 md:pb-8 transition-colors">
      
      {/* MOBILE VIEW: Clean Native Slider (Image 10 clone) */}
      <div className="md:hidden w-full mt-4 pb-2">
        <div className="px-4 mb-4">
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            In the Spotlight
          </h2>
        </div>
        <div 
          ref={scrollRef}
          onScroll={actualHandleNativeScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory px-4 pb-4 no-scrollbar items-center"
        >
          {slides.map((slide, idx) => {
            return (
              <div 
                key={`mob-hero-${slide.id}-${idx}`} 
                className="w-[calc(100vw-2rem)] shrink-0 snap-center cursor-pointer"
                onClick={() => onOpen(slide.id)}
              >
                <div className="bg-white dark:bg-slate-900 shadow-sm rounded-[16px] overflow-hidden pointer-events-none ring-1 ring-slate-200/50 dark:ring-slate-800">
                  <div className="relative aspect-[16/10] w-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                    <img 
                      src={slide.landscapeImage || slide.image} 
                      alt="" 
                      className="w-full h-full object-cover" 
                      loading="lazy" 
                    />
                  </div>
                  <div className="p-3.5">
                    <h3 className="font-bold text-slate-900 dark:text-white text-[15px] leading-tight line-clamp-1">
                      {slide.name || slide.title}
                    </h3>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wide">
                      {slide.category?.name || slide.language || "Entertainment"}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Pagination Dots (Mobile) */}
        {slides.length > 1 && (
          <div className="flex items-center justify-center gap-2 mt-2">
            {slides.map((_, idx) => (
              <div
                key={`dot-mob-${idx}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  safeActive === idx ? "w-2 bg-slate-900 dark:bg-white" : "w-2 bg-slate-400/50 dark:bg-slate-600"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* DESKTOP VIEW: Slider Container */}
      <div 
        className="hidden md:block relative group w-full overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex"
          style={{
            width: `${renderSlides.length * 100}%`,
            transform: `translateX(-${trackIndex * (100 / renderSlides.length)}%)`,
            transition: anim ? "transform 700ms ease-in-out" : "none",
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {renderSlides.map((slide, idx) => (
            <div
              key={`premium-${slide.id ?? "event"}-${idx}`}
              className="w-full shrink-0 px-0"
              style={{ width: `${100 / renderSlides.length}%` }}
              onClickCapture={() => setPaused(true)}
              onClick={() => onOpen(slide.id)}
            >
              <FeaturedBig item={slide} />
            </div>
          ))}
        </div>

        {/* Arrows sit on the far edges with solid chips so zoom never covers title/location text */}
        {slides.length > 1 && (
          <>
            <button
              type="button"
              onClick={handlePrev}
              aria-label="Previous featured event"
              className="absolute left-2 top-1/2 z-20 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full border border-slate-200/80 bg-white/95 text-slate-800 shadow-md opacity-0 transition-opacity duration-300 hover:bg-white group-hover:opacity-100 dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-100 dark:hover:bg-slate-900 md:left-3 lg:left-4"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleNext}
              aria-label="Next featured event"
              className="absolute right-2 top-1/2 z-20 -translate-y-1/2 grid h-10 w-10 place-items-center rounded-full border border-slate-200/80 bg-white/95 text-slate-800 shadow-md opacity-0 transition-opacity duration-300 hover:bg-white group-hover:opacity-100 dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-100 dark:hover:bg-slate-900 md:right-3 lg:right-4"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Pagination Dots */}
      {slides.length > 1 && (
        <div className="mt-4 hidden md:flex items-center justify-center gap-2">
          {slides.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setAnim(true);
                setActive(idx);
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                safeActive === idx ? "w-6 bg-slate-900 dark:bg-slate-100" : "w-2 bg-slate-300 dark:bg-slate-700 hover:bg-slate-400 dark:hover:bg-slate-500"
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
